import classNames from 'classnames';
import * as React from 'react';

import { ReactComponent as Logo } from './icons/logo.svg';
import { ReactComponent as ArrowDownLong } from './icons/arrow-down-long.svg';
import { ReactComponent as Check } from './icons/check.svg';
import { ReactComponent as CheckboxHover } from './icons/checkbox-hover.svg';
import { ReactComponent as CheckboxPressed } from './icons/checkbox-pressed.svg';
import { ReactComponent as Close } from './icons/close.svg';
import { ReactComponent as Dashboard } from './icons/dashboard.svg';
import { ReactComponent as ErrorCircle } from './icons/error-circle.svg';
import { ReactComponent as ExpandLess } from './icons/expand-less.svg';
import { ReactComponent as ExpandMore } from './icons/expand-more.svg';
import { ReactComponent as InfoCircle } from './icons/info-circle.svg';
import { ReactComponent as Sort } from './icons/sort.svg';
import { ReactComponent as SuccessCircle } from './icons/success-circle.svg';
import { ReactComponent as Times } from './icons/times.svg';
import { ReactComponent as WarningCircle } from './icons/warning-circle.svg';
import { ReactComponent as RadioDefault } from './icons/radio-default.svg';
import { ReactComponent as RadioSelected } from './icons/radio-selected.svg';
import { ReactComponent as RadioSelectedBlack } from './icons/radio-selected-black.svg';
import { ReactComponent as EyeHide } from './icons/eye-hide.svg';
import { ReactComponent as EyeShow } from './icons/eye-show.svg';
import { ReactComponent as Calendar } from './icons/calendar.svg';
import { ReactComponent as LogOut } from './icons/logout.svg';
import { ReactComponent as Save } from './icons/save.svg';
import { ReactComponent as Backspace } from './icons/backspace.svg';
import { ReactComponent as ArrowBack } from './icons/arrow-back.svg';
import { ReactComponent as Polygon } from './icons/polygon.svg';
import { ReactComponent as Point } from './icons/point.svg';
import { ReactComponent as Square } from './icons/square.svg';
import { ReactComponent as TagBox } from './icons/tag-box.svg';
import { ReactComponent as Segment } from './icons/segment.svg';
import { ReactComponent as ArrowDropLeftCircle } from './icons/arrow-dropleft-circle.svg';
import { ReactComponent as ArrowDropdown } from './icons/arrow-dropdown.svg';
import { ReactComponent as User } from './icons/user.svg';
import { ReactComponent as Group } from './icons/group.svg';
import { ReactComponent as UploadFile } from './icons/upload-file.svg';
import { ReactComponent as CloseIcon } from './icons/close-icon.svg';
import { ReactComponent as Reload } from './icons/reload.svg';
import { ReactComponent as CheckRound } from './icons/check-round.svg';
import { ReactComponent as Flag } from './icons/flag.svg';
import { ReactComponent as Search } from './icons/search.svg';
import { ReactComponent as UserInfo } from './icons/user_info.svg';
import { ReactComponent as Previous } from './icons/previous.svg';
import { ReactComponent as First } from './icons/first.svg';
import { ReactComponent as Plus } from './icons/plus.svg';
import { ReactComponent as Coin } from './icons/coin.svg';
import { ReactComponent as Undo } from './icons/undo.svg';
import { ReactComponent as Redo } from './icons/redo.svg';
import { ReactComponent as Pen } from './icons/pen.svg';
import { ReactComponent as Stars } from './icons/stars_icon.svg';
import { ReactComponent as PenRemove } from './icons/pen_remove.svg';

import './Icon.scss';

export const ICONS = {
  LOGO: Logo,
  DASHBOARD: Dashboard,
  CLOSE: Close,
  CHECK: Check,
  ERROR_CIRCLE: ErrorCircle,
  INFO_CIRCLE: InfoCircle,
  SUCCESS_CIRCLE: SuccessCircle,
  WARNING_CIRCLE: WarningCircle,
  EXPAND_MORE: ExpandMore,
  EXPAND_LESS: ExpandLess,
  TIMES: Times,
  SORT: Sort,
  ARROW_DOWN_LONG: ArrowDownLong,
  CHECKBOX_HOVER: CheckboxHover,
  CHECKBOX_PRESSED: CheckboxPressed,
  RADIO_DEFAULT: RadioDefault,
  RADIO_SELECTED: RadioSelected,
  RADIO_SELECTED_BLACK: RadioSelectedBlack,
  EYE_HIDE: EyeHide,
  EYE_SHOW: EyeShow,
  CALENDAR: Calendar,
  LOG_OUT: LogOut,
  SAVE: Save,
  BACKSPACE: Backspace,
  ARROW_BACK: ArrowBack,
  POLYGON: Polygon,
  POINT: Point,
  SEGMENT: Segment,
  SQUARE: Square,
  TAG_BOX: TagBox,
  ARROW_DROP_LEFT_CIRCLE: ArrowDropLeftCircle,
  ARROW_DROPDOWN: ArrowDropdown,
  USER: User,
  GROUP: Group,
  UPLOAD_FILE: UploadFile,
  CLOSE_ICON: CloseIcon,
  RELOAD: Reload,
  CHECK_ROUND: CheckRound,
  FLAG: Flag,
  SEARCH: Search,
  USER_INFO: UserInfo,
  PREVIOUS: Previous,
  FIRST: First,
  PLUS: Plus,
  COIN: Coin,
  UNDO: Undo,
  REDO: Redo,
  PEN: Pen,
  STARS: Stars,
  PEN_REMOVE: PenRemove
};

export interface IconProps extends React.SVGProps<HTMLDivElement> {
  /**
   * Use ICONS constant in Icon.tsx
   */
  component: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
  className?: string;
  containerClassName?: string;
  size?: IconSize;
  rotation?: number;
  style?: React.CSSProperties;
}

export enum IconSize {
  XS = 'XS',
  SM = 'SM',
  MD = 'MD',
  LG = 'LG',
  LLG = 'LLG',
  XL = 'XL',
  XXL = 'XXL',
  XXXL = 'XXXL',
}

const Icon: React.FC<IconProps> = ({ rotation, size = IconSize.SM, containerClassName, component: Component, ...rest }) => {
  const classes = classNames(
    'icon',
    {
      'icon--xs': size === IconSize.XS,
      'icon--sm': size === IconSize.SM,
      'icon--md': size === IconSize.MD,
      'icon--lg': size === IconSize.LG,
      'icon--llg': size === IconSize.LLG,
      'icon--xl': size === IconSize.XL,
      'icon--xxl': size === IconSize.XXL,
      'icon--xxxl': size === IconSize.XXXL,
    },

    rotation ? `icon--rotate-${rotation}` : '',
  );

  const containerClasses = classNames('icon__container', containerClassName);

  return (
    <div className={containerClasses} {...rest}>
      <Component className={classes} />
    </div>
  );
};

export default Icon;
